import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Trash2, Loader2, Send, FolderOpen, ChevronRight, ChevronDown,
  Upload, Copy, Clock, FileJson, X, Globe,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ── Types ─────────────────────────────────────────────────────────

interface ApiEndpoint {
  id: string
  collectionId: string
  name: string
  method: string
  path: string
  description: string | null
  headers: Record<string, string> | null
  queryParams: Record<string, string> | null
  bodyTemplate: Record<string, unknown> | null
  responseSchema: Record<string, unknown> | null
  sortOrder: number
  createdAt: string
  updatedAt: string
}

interface ApiCollection {
  id: string
  name: string
  description: string | null
  baseUrl: string | null
  authType: string | null
  authConfig: Record<string, unknown> | null
  headers: Record<string, string> | null
  variables: Record<string, string> | null
  isPublic: boolean
  endpoints: ApiEndpoint[]
  createdAt: string
  updatedAt: string
}

interface TestResult {
  status: number
  statusText: string
  headers: Record<string, string>
  body: unknown
  responseTime: number
  size: number
}

interface ImportResult {
  collectionId: string
  collectionName: string
  endpointsCreated: number
  errors: Array<{ path: string; error: string }>
}

interface KeyValuePair {
  key: string
  value: string
  enabled: boolean
}

// ── Helpers ───────────────────────────────────────────────────────

function methodColor(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET': return 'bg-green-500/10 text-green-600 border-green-500/20'
    case 'POST': return 'bg-blue-500/10 text-blue-600 border-blue-500/20'
    case 'PUT': return 'bg-orange-500/10 text-orange-600 border-orange-500/20'
    case 'DELETE': return 'bg-red-500/10 text-red-600 border-red-500/20'
    case 'PATCH': return 'bg-purple-500/10 text-purple-600 border-purple-500/20'
    default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20'
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function kvToRecord(pairs: KeyValuePair[]): Record<string, string> {
  const result: Record<string, string> = {}
  for (const p of pairs) {
    if (p.enabled && p.key.trim()) {
      result[p.key.trim()] = p.value
    }
  }
  return result
}

// ── Key-Value Editor ──────────────────────────────────────────────

function KeyValueEditor({
  pairs,
  onChange,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
}: {
  pairs: KeyValuePair[]
  onChange: (pairs: KeyValuePair[]) => void
  keyPlaceholder?: string
  valuePlaceholder?: string
}) {
  const addPair = () => {
    onChange([...pairs, { key: '', value: '', enabled: true }])
  }

  const updatePair = (index: number, updates: Partial<KeyValuePair>) => {
    const updated = [...pairs]
    updated[index] = { ...updated[index], ...updates }
    onChange(updated)
  }

  const removePair = (index: number) => {
    onChange(pairs.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-2">
      {pairs.map((pair, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={pair.enabled}
            onChange={(e) => updatePair(i, { enabled: e.target.checked })}
            className="h-4 w-4 rounded border-input"
          />
          <Input
            placeholder={keyPlaceholder}
            value={pair.key}
            onChange={(e) => updatePair(i, { key: e.target.value })}
            className="flex-1 h-8 text-sm"
          />
          <Input
            placeholder={valuePlaceholder}
            value={pair.value}
            onChange={(e) => updatePair(i, { value: e.target.value })}
            className="flex-1 h-8 text-sm"
          />
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removePair(i)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addPair} className="w-full">
        <Plus className="h-3 w-3 mr-1" /> Add
      </Button>
    </div>
  )
}

// ── Create Collection Dialog ──────────────────────────────────────

function CreateCollectionDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [description, setDescription] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/api-hub/collections', data),
    onSuccess: () => {
      onCreated()
      toast({ title: 'Created', description: 'Collection created successfully' })
      setOpen(false)
      setName('')
      setBaseUrl('')
      setDescription('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create collection', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="mr-1 h-3 w-3" /> Collection</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New API Collection</DialogTitle>
          <DialogDescription>Create a new collection to organize your API endpoints.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My API" />
          </div>
          <div className="space-y-2">
            <Label>Base URL</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="API description..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate({ name, baseUrl: baseUrl || undefined, description: description || undefined })}
            disabled={!name || mutation.isPending}
          >
            {mutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Import Dialog ─────────────────────────────────────────────────

function ImportSpecDialog({ onImported }: { onImported: () => void }) {
  const [open, setOpen] = useState(false)
  const [specText, setSpecText] = useState('')
  const [collectionName, setCollectionName] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { spec: Record<string, unknown>; collectionName?: string }) =>
      apiClient.post<ImportResult>('/api-hub/import', data),
    onSuccess: (data) => {
      onImported()
      const result = data?.data
      toast({
        title: 'Imported',
        description: `Created ${result?.endpointsCreated || 0} endpoints${result?.errors?.length ? ` with ${result.errors.length} errors` : ''}`,
      })
      setOpen(false)
      setSpecText('')
      setCollectionName('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to import spec', variant: 'destructive' }),
  })

  const handleImport = () => {
    try {
      const spec = JSON.parse(specText)
      mutation.mutate({ spec, collectionName: collectionName || undefined })
    } catch {
      toast({ title: 'Error', description: 'Invalid JSON', variant: 'destructive' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"><Upload className="mr-1 h-3 w-3" /> Import</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import OpenAPI / Swagger</DialogTitle>
          <DialogDescription>Paste your OpenAPI 3.x or Swagger 2.x JSON specification below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Collection Name (optional)</Label>
            <Input value={collectionName} onChange={(e) => setCollectionName(e.target.value)} placeholder="Override auto-detected name..." />
          </div>
          <div className="space-y-2">
            <Label>Specification (JSON)</Label>
            <Textarea
              value={specText}
              onChange={(e) => setSpecText(e.target.value)}
              placeholder='{"openapi": "3.0.0", "info": {...}, "paths": {...}}'
              className="font-mono text-xs h-64"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleImport} disabled={!specText || mutation.isPending}>
            {mutation.isPending ? 'Importing...' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Add Endpoint Dialog ───────────────────────────────────────────

function AddEndpointDialog({ collectionId, onAdded }: { collectionId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [method, setMethod] = useState('GET')
  const [path, setPath] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`/api-hub/collections/${collectionId}/endpoints`, data),
    onSuccess: () => {
      onAdded()
      toast({ title: 'Added', description: 'Endpoint added successfully' })
      setOpen(false)
      setName('')
      setPath('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to add endpoint', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
          <Plus className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Endpoint</DialogTitle>
          <DialogDescription>Add a new API endpoint to this collection.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Get Users" />
          </div>
          <div className="flex gap-3">
            <div className="w-28">
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Path</Label>
              <Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/users" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate({ name, method, path })}
            disabled={!name || !path || mutation.isPending}
          >
            {mutation.isPending ? 'Adding...' : 'Add Endpoint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Collection Tree ───────────────────────────────────────────────

function CollectionTree({
  collections,
  selectedEndpoint,
  onSelectEndpoint,
  onRefresh,
}: {
  collections: ApiCollection[]
  selectedEndpoint: { collection: ApiCollection; endpoint: ApiEndpoint } | null
  onSelectEndpoint: (collection: ApiCollection, endpoint: ApiEndpoint) => void
  onRefresh: () => void
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const { toast } = useToast()

  const deleteCollectionMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api-hub/collections/${id}`),
    onSuccess: () => {
      onRefresh()
      toast({ title: 'Deleted', description: 'Collection deleted' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' }),
  })

  const deleteEndpointMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api-hub/endpoints/${id}`),
    onSuccess: () => {
      onRefresh()
      toast({ title: 'Deleted', description: 'Endpoint deleted' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' }),
  })

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // Auto-expand first collection
  useEffect(() => {
    if (collections.length > 0 && Object.keys(expanded).length === 0) {
      setExpanded({ [collections[0].id]: true })
    }
  }, [collections])

  return (
    <div className="space-y-1">
      {collections.map((collection) => (
        <div key={collection.id}>
          <div className="flex items-center gap-1 py-1 px-2 rounded-md hover:bg-accent group">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0"
              onClick={() => toggleExpand(collection.id)}
            >
              {expanded[collection.id] ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
            <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium truncate flex-1" title={collection.name}>{collection.name}</span>
            <div className="hidden group-hover:flex items-center gap-0.5">
              <AddEndpointDialog collectionId={collection.id} onAdded={onRefresh} />
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-destructive"
                onClick={() => deleteCollectionMutation.mutate(collection.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {expanded[collection.id] && (
            <div className="ml-6 space-y-0.5">
              {collection.endpoints.map((endpoint) => {
                const isSelected = selectedEndpoint?.endpoint.id === endpoint.id
                return (
                  <div
                    key={endpoint.id}
                    className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer group ${
                      isSelected ? 'bg-accent' : 'hover:bg-accent/50'
                    }`}
                    onClick={() => onSelectEndpoint(collection, endpoint)}
                  >
                    <Badge className={`${methodColor(endpoint.method)} text-[10px] font-bold px-1.5 py-0 h-5 shrink-0`}>
                      {endpoint.method}
                    </Badge>
                    <span className="text-sm truncate flex-1" title={endpoint.name}>{endpoint.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hidden group-hover:flex h-5 w-5 p-0 text-destructive shrink-0"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteEndpointMutation.mutate(endpoint.id)
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )
              })}
              {collection.endpoints.length === 0 && (
                <p className="text-xs text-muted-foreground px-2 py-1">No endpoints</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Request Builder ───────────────────────────────────────────────

function RequestBuilder({
  collection,
  endpoint,
  onRefresh: _onRefresh,
}: {
  collection: ApiCollection
  endpoint: ApiEndpoint
  onRefresh: () => void
}) {
  const [method, setMethod] = useState(endpoint.method)
  const [url, setUrl] = useState('')
  const [headers, setHeaders] = useState<KeyValuePair[]>([])
  const [queryParams, setQueryParams] = useState<KeyValuePair[]>([])
  const [bodyText, setBodyText] = useState('')
  const [bodyTab, setBodyTab] = useState('params')
  const [result, setResult] = useState<TestResult | null>(null)
  const [responseTab, setResponseTab] = useState('body')
  const { toast } = useToast()

  // Re-initialize when endpoint changes
  useEffect(() => {
    setMethod(endpoint.method)
    const base = collection.baseUrl?.replace(/\/$/, '') || ''
    setUrl(`${base}${endpoint.path}`)
    setResult(null)

    // Initialize headers from endpoint and collection defaults
    const defaultHeaders: KeyValuePair[] = []
    if (collection.headers) {
      for (const [key, value] of Object.entries(collection.headers)) {
        defaultHeaders.push({ key, value, enabled: true })
      }
    }
    if (endpoint.headers) {
      for (const [key, value] of Object.entries(endpoint.headers)) {
        defaultHeaders.push({ key, value, enabled: true })
      }
    }
    if (defaultHeaders.length === 0) {
      defaultHeaders.push({ key: '', value: '', enabled: true })
    }
    setHeaders(defaultHeaders)

    // Initialize query params
    const defaultParams: KeyValuePair[] = []
    if (endpoint.queryParams) {
      for (const [key, value] of Object.entries(endpoint.queryParams)) {
        defaultParams.push({ key, value, enabled: true })
      }
    }
    if (defaultParams.length === 0) {
      defaultParams.push({ key: '', value: '', enabled: true })
    }
    setQueryParams(defaultParams)

    // Initialize body
    if (endpoint.bodyTemplate) {
      setBodyText(JSON.stringify(endpoint.bodyTemplate, null, 2))
    } else {
      setBodyText('')
    }
  }, [endpoint.id])

  const sendMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post<TestResult>('/api-hub/test', data),
    onSuccess: (data) => {
      setResult(data?.data || null)
      setResponseTab('body')
    },
    onError: () => toast({ title: 'Error', description: 'Request failed', variant: 'destructive' }),
  })

  const handleSend = () => {
    const headerRecord = kvToRecord(headers)
    const paramRecord = kvToRecord(queryParams)

    let body: unknown = undefined
    if (bodyText && method !== 'GET') {
      try {
        body = JSON.parse(bodyText)
      } catch {
        body = bodyText
      }
    }

    sendMutation.mutate({
      method,
      url,
      headers: Object.keys(headerRecord).length > 0 ? headerRecord : undefined,
      queryParams: Object.keys(paramRecord).length > 0 ? paramRecord : undefined,
      body,
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* URL Bar */}
      <div className="flex gap-2 mb-4">
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="flex-1 font-mono text-sm"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://api.example.com/endpoint"
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <Button onClick={handleSend} disabled={!url || sendMutation.isPending}>
          {sendMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          <span className="ml-2">Send</span>
        </Button>
      </div>

      {/* Request Configuration */}
      <Tabs value={bodyTab} onValueChange={setBodyTab} className="flex-1 min-h-0">
        <TabsList>
          <TabsTrigger value="params">
            Params
            {queryParams.filter((p) => p.enabled && p.key).length > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                {queryParams.filter((p) => p.enabled && p.key).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="headers">
            Headers
            {headers.filter((h) => h.enabled && h.key).length > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                {headers.filter((h) => h.enabled && h.key).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="body">Body</TabsTrigger>
        </TabsList>

        <div className="mt-3">
          <TabsContent value="params" className="mt-0">
            <KeyValueEditor
              pairs={queryParams}
              onChange={setQueryParams}
              keyPlaceholder="Parameter"
              valuePlaceholder="Value"
            />
          </TabsContent>
          <TabsContent value="headers" className="mt-0">
            <KeyValueEditor
              pairs={headers}
              onChange={setHeaders}
              keyPlaceholder="Header"
              valuePlaceholder="Value"
            />
          </TabsContent>
          <TabsContent value="body" className="mt-0">
            <Textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder='{"key": "value"}'
              className="font-mono text-xs min-h-[120px]"
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Response */}
      {result && (
        <>
          <Separator className="my-4" />
          <div className="flex-1 min-h-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-sm">Response</h3>
                <Badge variant={result.status < 300 ? 'default' : result.status < 400 ? 'secondary' : 'destructive'}>
                  {result.status} {result.statusText}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{result.responseTime}ms</span>
                <span>{formatSize(result.size)}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2)
                    )
                    toast({ title: 'Copied', description: 'Response copied to clipboard' })
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
            </div>

            <Tabs value={responseTab} onValueChange={setResponseTab}>
              <TabsList>
                <TabsTrigger value="body">Body</TabsTrigger>
                <TabsTrigger value="headers">
                  Headers
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {Object.keys(result.headers).length}
                  </Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="body" className="mt-2">
                <ScrollArea className="h-[250px] w-full">
                  <pre className="text-xs font-mono bg-muted/50 rounded-md p-3 whitespace-pre-wrap break-all">
                    {typeof result.body === 'string'
                      ? result.body
                      : JSON.stringify(result.body, null, 2)}
                  </pre>
                </ScrollArea>
              </TabsContent>
              <TabsContent value="headers" className="mt-2">
                <ScrollArea className="h-[250px] w-full">
                  <div className="space-y-1">
                    {Object.entries(result.headers).map(([key, value]) => (
                      <div key={key} className="flex gap-2 bg-muted/50 rounded-md px-3 py-1.5">
                        <span className="text-xs font-medium text-muted-foreground w-48 shrink-0 truncate">{key}</span>
                        <span className="text-xs font-mono truncate">{value}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────

export function ApiHubPage() {
  const queryClient = useQueryClient()
  const [selectedEndpoint, setSelectedEndpoint] = useState<{
    collection: ApiCollection
    endpoint: ApiEndpoint
  } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['api-hub', 'collections'],
    queryFn: () => apiClient.get<ApiCollection[]>('/api-hub/collections'),
  })

  const collections = data?.data || []

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['api-hub', 'collections'] })
  }

  const handleSelectEndpoint = (collection: ApiCollection, endpoint: ApiEndpoint) => {
    setSelectedEndpoint({ collection, endpoint })
  }

  return (
    <div className="space-y-4 h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Hub</h1>
          <p className="text-muted-foreground">API documentation and endpoint testing</p>
        </div>
        <div className="flex items-center gap-2">
          <ImportSpecDialog onImported={handleRefresh} />
          <CreateCollectionDialog onCreated={handleRefresh} />
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100%-5rem)]">
        {/* Left Panel - Collection Tree */}
        <Card className="w-72 shrink-0 flex flex-col">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileJson className="h-4 w-4" />
              Collections
              <Badge variant="secondary" className="ml-auto">{collections.length}</Badge>
            </CardTitle>
          </CardHeader>
          <Separator />
          <ScrollArea className="flex-1">
            <div className="p-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : collections.length === 0 ? (
                <div className="text-center py-8">
                  <Globe className="h-8 w-8 mx-auto mb-2 opacity-50 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">No collections yet</p>
                  <p className="text-xs text-muted-foreground">Create one or import a spec</p>
                </div>
              ) : (
                <CollectionTree
                  collections={collections}
                  selectedEndpoint={selectedEndpoint}
                  onSelectEndpoint={handleSelectEndpoint}
                  onRefresh={handleRefresh}
                />
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Right Panel - Request Builder */}
        <Card className="flex-1 flex flex-col">
          <CardContent className="flex-1 pt-4 pb-4 overflow-auto">
            {selectedEndpoint ? (
              <RequestBuilder
                collection={selectedEndpoint.collection}
                endpoint={selectedEndpoint.endpoint}
                onRefresh={handleRefresh}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Send className="h-16 w-16 mb-4 opacity-20 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">Select an endpoint</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose an endpoint from the collection tree to start testing
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
